import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  Calendar,
  ChevronRight,
  Eye,
  Funnel,
  LogIn,
  LUCIDE_ICONS,
  LucideAngularModule,
  LucideIconProvider,
  Moon,
  PlusCircle,
  X,
} from 'lucide-angular';
import { Concurso, EstadoConcursoInicio } from '../../../../core/models/inicio.models';
import { RolPlataforma } from '../../../../core/models/novedades.models';
import { InicioService } from '../../../../core/services/inicio.service';
import { PostulacionesService } from '../../../../core/services/postulaciones.service';
import { formatShortDate } from '../../../../core/utils/date-format.util';
import { normalizeSearchText } from '../../../../core/utils/text-format.util';
import { environment } from '../../../../../environments/environment';
import { EmptyFilterState } from '../../../../shared/components/empty-filter-state/empty-filter-state';
import { TablePagination } from '../../../../shared/components/table-pagination/table-pagination';

type FiltroAvanzadoKey = 'busqueda' | 'id' | 'subdireccion' | 'convocatoria';

interface FiltroAvanzadoChip {
  key: FiltroAvanzadoKey;
  label: string;
  removable: boolean;
}

@Component({
  selector: 'app-inicio-concursos-panel',
  imports: [CommonModule, FormsModule, LucideAngularModule, EmptyFilterState, TablePagination],
  providers: [
    {
      provide: LUCIDE_ICONS,
      multi: true,
      useValue: new LucideIconProvider({
        ChevronRight,
        Moon,
        PlusCircle,
        X,
        Funnel,
        Eye,
        Calendar,
        LogIn,
      }),
    },
  ],
  templateUrl: './inicio-concursos-panel.html',
  styleUrl: './inicio-concursos-panel.css',
})
export class InicioConcursosPanel implements OnInit {
  @Input({ required: true }) rolSeleccionado: RolPlataforma = 'postulante';
  @Input() mostrarFiltrosPostulaciones = false;
  readonly mesaAyudaUrl = environment.mesaAyudaPostulacionesUrl;

  concursos: Concurso[] = [];
  concursosOriginales: Concurso[] = [];
  concursosFiltrados: Concurso[] = [];
  resultados: Concurso[] = [];
  filtros = {
    idPostulacion: '',
    subdireccion: '',
    convocatoria: '',
  };
  terminoBusqueda = '';
  mensajeBusqueda = '';
  subdirecciones: string[] = [];
  convocatorias: string[] = [];
  idPostulacionFiltro = '';
  subdireccionSeleccionada = 'Todas';
  convocatoriaSeleccionada = 'Todas';
  ordenSeleccionado: 'inicio_desc' | 'inicio_asc' | 'fin_desc' | 'fin_asc' = 'fin_asc';
  mensajeAccion = '';
  paginaActual = 1;
  tamanoPagina = 10;
  tamanosPaginaDisponibles: number[] = [10, 20, 30, 40, 50];

  readonly minimoBusqueda = 4;

  constructor(
    private readonly inicioService: InicioService,
    private readonly postulacionesService: PostulacionesService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    // Carga catalogo completo de concursos para mostrar convocatorias abiertas y cerradas.
    this.inicioService.getConcursos().subscribe((data) => {
      this.concursos = data;
      this.concursosOriginales = [...data];
      this.concursosFiltrados = [...this.concursosOriginales];
      this.subdirecciones = [...new Set(data.map((c) => c.subdireccion))];
      this.convocatorias = [...new Set(data.map((c) => c.convocatoria))];
      this.aplicarFiltros();
    });
  }

  buscar(valor: string): void {
    this.terminoBusqueda = valor.trim();
    this.aplicarFiltros();
  }

  limpiarFiltros(): void {
    if (this.mostrarFiltrosPostulaciones) {
      this.filtros = {
        idPostulacion: '',
        subdireccion: '',
        convocatoria: '',
      };
      this.concursosFiltrados = [...this.concursosOriginales];
      this.resultados = this.ordenarConcursos(this.concursosFiltrados);
      this.paginaActual = 1;
      this.mensajeBusqueda = '';
      this.mensajeAccion = '';
      return;
    }

    this.terminoBusqueda = '';
    this.idPostulacionFiltro = '';
    this.subdireccionSeleccionada = 'Todas';
    this.convocatoriaSeleccionada = 'Todas';
    this.ordenSeleccionado = 'fin_asc';
    this.mensajeBusqueda = '';
    this.mensajeAccion = '';
    this.aplicarFiltros();
  }

  aplicarFiltros(): void {
    if (this.mostrarFiltrosPostulaciones) {
      this.aplicarFiltrosPostulaciones();
      return;
    }

    // Flujo de filtrado: catalogo completo -> selectores -> busqueda -> orden final.
    this.paginaActual = 1;
    let base = [...this.concursos];

    if (this.idPostulacionFiltro.trim()) {
      const idNormalizado = normalizeSearchText(this.idPostulacionFiltro);
      base = base.filter((c) => normalizeSearchText(c.id).includes(idNormalizado));
    }

    if (this.subdireccionSeleccionada !== 'Todas') {
      base = base.filter((c) => c.subdireccion === this.subdireccionSeleccionada);
    }

    if (this.convocatoriaSeleccionada !== 'Todas') {
      base = base.filter((c) => c.convocatoria === this.convocatoriaSeleccionada);
    }

    if (!this.terminoBusqueda) {
      this.resultados = this.ordenarConcursos(base);
      this.mensajeBusqueda = base.length === 0 ? 'No se encontraron concursos para los filtros seleccionados.' : '';
      return;
    }

    if (this.terminoBusqueda.length < this.minimoBusqueda) {
      this.resultados = this.ordenarConcursos(base);
      this.mensajeBusqueda = `Ingresa al menos ${this.minimoBusqueda} caracteres para buscar.`;
      return;
    }

    const normalizado = normalizeSearchText(this.terminoBusqueda);
    const filtrados = base.filter((concurso) => {
      const nombre = normalizeSearchText(concurso.nombre);
      const subdireccion = normalizeSearchText(concurso.subdireccion);
      const convocatoria = normalizeSearchText(concurso.convocatoria);
      const palabras = concurso.palabrasClave.map((p) => normalizeSearchText(p));

      return (
        nombre.includes(normalizado) ||
        subdireccion.includes(normalizado) ||
        convocatoria.includes(normalizado) ||
        palabras.some((p) => p.includes(normalizado))
      );
    });
    this.resultados = this.ordenarConcursos(filtrados);

    this.mensajeBusqueda = this.resultados.length === 0 ? 'No se encontraron concursos con la busqueda y filtros aplicados.' : '';
  }

  get resultadosPaginados(): Concurso[] {
    const inicio = (this.paginaActual - 1) * this.tamanoPagina;
    const fin = inicio + this.tamanoPagina;
    return this.resultados.slice(inicio, fin);
  }

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.resultados.length / this.tamanoPagina));
  }

  get filtrosActivos(): FiltroAvanzadoChip[] {
    const chips: FiltroAvanzadoChip[] = [];

    if (this.terminoBusqueda.trim().length >= this.minimoBusqueda) {
      chips.push({
        key: 'busqueda',
        label: `Busqueda: ${this.terminoBusqueda.trim()}`,
        removable: true,
      });
    }

    if (this.idPostulacionFiltro.trim()) {
      chips.push({
        key: 'id',
        label: `ID: ${this.idPostulacionFiltro.trim()}`,
        removable: true,
      });
    }

    if (this.subdireccionSeleccionada !== 'Todas') {
      chips.push({
        key: 'subdireccion',
        label: this.subdireccionSeleccionada,
        removable: true,
      });
    }

    if (this.convocatoriaSeleccionada !== 'Todas') {
      chips.push({
        key: 'convocatoria',
        label: this.convocatoriaSeleccionada,
        removable: true,
      });
    }

    return chips;
  }

  cambiarTamanoPagina(): void {
    this.paginaActual = 1;
  }

  cambiarPagina(pagina: number): void {
    this.paginaActual = pagina;
  }

  cambiarTamanoPaginaDesdePaginador(tamano: number): void {
    this.tamanoPagina = tamano;
    this.cambiarTamanoPagina();
  }

  abrirMesaAyuda(event?: Event): void {
    if (this.mesaAyudaUrl.trim()) {
      this.mensajeAccion = '';
      return;
    }

    event?.preventDefault();
    this.mensajeAccion = 'La URL oficial de Mesa de Ayuda ANID debe ser configurada por la empresa integradora.';
  }

  quitarFiltro(key: FiltroAvanzadoKey): void {
    switch (key) {
      case 'busqueda':
        this.terminoBusqueda = '';
        break;
      case 'id':
        this.idPostulacionFiltro = '';
        break;
      case 'subdireccion':
        this.subdireccionSeleccionada = 'Todas';
        break;
      case 'convocatoria':
        this.convocatoriaSeleccionada = 'Todas';
        break;
      default:
        return;
    }

    this.aplicarFiltros();
  }

  formatearFecha(fecha: string): string {
    return formatShortDate(fecha, fecha);
  }

  verConcurso(concurso: Concurso): void {
    // Abre detalle en nueva pestaña cuando existe URL asociada.
    const url = concurso.urlDetalle?.trim();
    if (!url) {
      this.mensajeAccion = 'El detalle del concurso no esta disponible por ahora.';
      return;
    }

    this.mensajeAccion = '';
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  puedeIniciarPostulacion(concurso: Concurso): boolean {
    return this.rolSeleccionado === 'postulante' && concurso.estado === 'abierto';
  }

  estadoConcursoLabel(estado: EstadoConcursoInicio): string {
    if (estado === 'cerrado') return 'Cerrado';
    if (estado === 'en_evaluacion') return 'En evaluacion';
    return 'Abierto';
  }

  estadoConcursoClase(estado: EstadoConcursoInicio): string {
    if (estado === 'cerrado') return 'badge-closed';
    if (estado === 'en_evaluacion') return 'badge-review';
    return 'badge-open';
  }

  iniciarPostulacion(concurso: Concurso): void {
    if (!this.puedeIniciarPostulacion(concurso)) {
      this.mensajeAccion = 'Solo se puede iniciar postulacion en convocatorias abiertas.';
      return;
    }

    this.mensajeAccion = '';

    this.postulacionesService.iniciarPostulacionDesdeConcurso(concurso).subscribe((postulacion) => {
      if (!postulacion) {
        this.mensajeAccion = 'No fue posible iniciar la postulacion desde este concurso.';
        return;
      }

      this.mensajeAccion = '';
      this.router.navigate(['/postulaciones/formulario', postulacion.idPostulacion], {
        queryParams: { role: this.rolSeleccionado },
      });
    });
  }

  private aplicarFiltrosPostulaciones(): void {
    this.paginaActual = 1;

    const idPostulacion = normalizeSearchText(this.filtros.idPostulacion);
    const subdireccion = normalizeSearchText(this.filtros.subdireccion);
    const convocatoria = normalizeSearchText(this.filtros.convocatoria);

    this.concursosFiltrados = this.concursosOriginales.filter((concurso) => {
      const cumpleId = !idPostulacion || normalizeSearchText(concurso.id).includes(idPostulacion);
      const cumpleSubdireccion = !subdireccion || normalizeSearchText(concurso.subdireccion).includes(subdireccion);
      const cumpleConvocatoria = !convocatoria || normalizeSearchText(concurso.convocatoria).includes(convocatoria);

      return cumpleId && cumpleSubdireccion && cumpleConvocatoria;
    });

    this.resultados = this.ordenarConcursos(this.concursosFiltrados);
    this.mensajeBusqueda = this.resultados.length === 0 ? 'No se encontraron concursos para los filtros seleccionados.' : '';
  }

  private ordenarConcursos(data: Concurso[]): Concurso[] {
    const copy = [...data];
    const getTime = (fecha: string): number => new Date(fecha).getTime();

    switch (this.ordenSeleccionado) {
      case 'inicio_asc':
        return copy.sort((a, b) => getTime(a.fechaApertura) - getTime(b.fechaApertura));
      case 'fin_desc':
        return copy.sort((a, b) => getTime(b.fechaCierre) - getTime(a.fechaCierre));
      case 'fin_asc':
        return copy.sort((a, b) => getTime(a.fechaCierre) - getTime(b.fechaCierre));
      case 'inicio_desc':
      default:
        return copy.sort((a, b) => getTime(b.fechaApertura) - getTime(a.fechaApertura));
    }
  }
}
